// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { Point, Rectangle } from '@shared/commons/Geometry';
import { Cursors } from '@shared/commons/Cursors';
import { Pen, Pens, RectangleEdgeFilter, SolidBrush } from '@shared/commons/Graphics';
import { ProMath, ToolView, MovingType } from '../ToolView';
import { SelectionState, ToolSelectionType } from '../Selection';
import { TerceraChartAction, TerceraChartActionEnum } from '../../TerceraChartAction';
import { TerceraChartTradingOperation } from '../../Utils/ChartConstants';
import { TradingToolViewBase, TradingToolViewType } from './TradingToolViewBase';
import { OrderToolViewBase } from './OrderToolViewBase';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { MathUtils } from '@shared/utils/MathUtils';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { OffsetModeViewEnum } from '@shared/utils/Trading/OffsetModeViewEnum';
import { SlTpPriceType } from '@shared/utils/Enums/Constants';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { LayersEnum } from '../../Utils/LayersEnum';
import { NO_BIND_TO } from '@shared/utils/Trading/OrderConstants';
import { OrderFormatter } from '@shared/commons/cache/Formatters/OrderFormatter';
export class OrderToolView extends OrderToolViewBase {
    get isAllowOTHTrading() {
        return this.Order.IsAllowOTHTrading;
    }
    get isAllowOTHTradingSLTP() {
        return this.isAllowOTHTrading;
    }
    constructor(order, getToolViewByOrderId, terceraChartPanelContext, renderer) {
        super();
        this.errorMode = false;
        // visual trading order/position place SL/TP
        this.slRect = new Rectangle();
        this.tpRect = new Rectangle();
        this.slPlased = false;
        this.tpPlased = false;
        this.slPriceValue = NaN;
        this.tpPriceValue = NaN;
        this.slPriceY = -1;
        this.tpPriceY = -1;
        this.priceY = -1;
        this.stopLimitY = -1;
        this.tpTrade = null;
        this.slTrade = null;
        this.sllTrade = null;
        this.isOrderToolView = true;
        this.modifyInProgress = false;
        this.activeQtyValue = null;
        this.linkedOrderToolTipRect = Rectangle.Empty();
        this.isDrawOrdersLinking = false;
        this.isQtyChanging = false;
        this.Order = order;
        /// <summary>
        /// получить трейд-тулзу по ай-ди из рендерера тулз
        /// </summary>
        this.GetToolViewByOrderId = getToolViewByOrderId;
        /// <summary>
        /// интерфейс чарт панели для модифая
        /// </summary>
        this.terceraChartPanelContext = terceraChartPanelContext;
        this.myRenderer = renderer;
        this.movingType = MovingType.OnlyY;
        this.Updated(order);
        this.collapsed = false;
        this.ToolViewType = TradingToolViewType.OrderToolView;
    }
    // #region properties
    AllowCancelOrder() {
        return IsAllowed.IsOrderCancelingAllowed(this.Order).Allowed;
    }
    AllowChangeToMarket() {
        return IsAllowed.IsOrderExecutingAllowed(this.Order).Allowed;
    }
    IsPositionToolView() {
        return this.Order ? this.Order.isPosition : false;
    }
    AllowModify() {
        // TODO
        // var route = this.Order.DataCache.Routes.getRouteByName(Order.Route);
        // var allowModifyPrice = this.Order.DataCache.isAllowedForMainAccount(RulesSet.FUNCTION_MODIFY_PRICE) && (route == null || route.AllowPriceModify);
        return IsAllowed.IsOrderModifyingAllowed(this.Order).Allowed; // && allowModifyPrice;
    }
    AllowSLTP(isSL) {
        const o = this.Order;
        const isSLParamComes = !(isNaN(isSL) || isSL === null);
        let result = IsAllowed.IsOrderSLTPAllowed(o).Allowed;
        if (isSLParamComes) {
            result = result && IsAllowed.IsSLTPAllowed(o.Instrument, o.Account, isSL).Allowed;
        }
        return result;
    }
    AllowClosePosition() {
        return false;
    }
    get Collapsed() {
        return this.collapsed;
    }
    set Collapsed(value) {
        if (this.collapsed != value) {
            this.collapsed = value;
        }
    }
    get ID() {
        return this.Order != null ? this.Order.OrderNumber : '-2';
    }
    get Price() {
        return this._getPrice();
    }
    _getPrice() {
        if (isNullOrUndefined(this.Order)) {
            return -1;
        }
        return this.Order.OrderType === OrderType.StopLimit ? this.Order.StopLimit : this.Order.Price;
    }
    SlComparePrice() {
        return this.Order.Price;
    }
    TpComparePrice() {
        return this.Order.Price;
    }
    getLeverageText() {
        return OrderUtils.GetFormattedLeverage(this.Order.Leverage);
    }
    // #endregion
    /// <summary>
    /// пришло с сервера - тулза обновилась сбрасываем серую ожидалку
    /// </summary>
    Updated(newOrder, isOrderDataUpdate) {
        if (this.Order == null || !newOrder) {
            return;
        }
        this.Order = newOrder;
        this.dataCacheTool.Points[0][0] = this.Order.UTCDateTime;
        this.dataCacheTool.Points[0][1] = this.Price;
        if (!this.CurrentMovement.IsMovingRightNow && !isOrderDataUpdate) {
            this.CancelMoving();
        }
        this.myRenderer.chart.IsDirty(LayersEnum.Tools);
    }
    IsDirty() {
        this.myRenderer.chart.IsDirty(LayersEnum.Tools);
    }
    CancelMoving() {
        this.slPlased = false;
        this.tpPlased = false;
        if (this.tpTrade) {
            this.tpTrade.ResetMove();
            this.tpTrade.CancelMoving();
        }
        if (this.slTrade) {
            this.slTrade.ResetMove();
            this.slTrade.CancelMoving();
        }
        if (this.sllTrade) {
            this.sllTrade.ResetMove();
            this.sllTrade.CancelMoving();
        }
        this.ResetMouse();
        this.TradeWasModified = false;
    }
    // TODO. Rename. Refactor.
    undoChanges() {
        this.CancelMove();
        this.Updated(this.Order, false);
    }
    /// <summary>
    /// текст инфо для расхлопнутой тулзы
    /// </summary>
    DescriptionText() {
        if (this.modifyInProgress && this.activeQtyValue !== null) {
            return OrderFormatter.FormatAmount(this.Order, this.activeQtyValue);
        }
        else {
            return OrderFormatter.AmountStr(this.Order);
        }
    }
    Draw(gr, window, param) {
        var _a;
        this.MainOrderVisibility = false;
        this.isDrawOrdersLinking = false;
        if ((!this.myRenderer.ShowPositions && !this.isOrderToolView) ||
            (!this.myRenderer.ShowOrders && this.isOrderToolView)) {
            return;
        }
        if (!((_a = this.Order) === null || _a === void 0 ? void 0 : _a.Price)) {
            return;
        }
        const CR = window.ClientRectangle;
        const minX = CR.X;
        const maxX = minX + CR.Width;
        try {
            const screenPoints01 = this.screenPoints[0][1];
            const screenPoints00 = this.getCorrectOrderX(this.screenPoints[0][0], minX);
            // Ловим ошибки
            if ((screenPoints00 < 0 && maxX < 0) ||
                screenPoints01 >= ProMath.infinity ||
                screenPoints01 >= ProMath.infinity ||
                screenPoints01 <= ProMath.infinityMinus ||
                screenPoints01 <= ProMath.infinityMinus) {
                return;
            }
            let curX = screenPoints00;
            const curY = screenPoints01 - this.containerHeight / 2;
            // оптимизация - вне не рисуем ордер и не добавляем его плашку
            if (!CR.Contains(screenPoints00, screenPoints01)) {
                this.clearAllRectangles();
                return;
            }
            const separators = [];
            this.MainOrderVisibility = true;
            const hoveredOrSelected = this.CurrentSelection.CurrentState !== SelectionState.None || this.isQtyChanging;
            const isBuy = this.Order.BuySell === OperationType.Buy;
            const isPosition = this.Order.isPosition;
            const threeDotsMenuVisible = this.AllowChangeToMarket() && !isPosition; // todo: выяснить почему у .Net позиции отсекаются в IsAllowed.IsOrderExecutingAllowed а у нас нет
            const im = this.getOrderToolIcon(hoveredOrSelected);
            this.imageRect = new Rectangle(curX, curY, this.btnSize, this.btnSize);
            curX += this.imageRect.Width;
            const toolIconText = this.getToolIconText(this.Order.OrderType);
            if (isValidString(toolIconText)) {
                curX = this.addTextRectWithSeparator(gr, toolIconText, curX, curY, separators, (r) => {
                    this.toolIconTextRect = r;
                });
            }
            else {
                this.toolIconTextRect = Rectangle.Empty();
            }
            //#region IsAllowOTHTrading
            if (this.isAllowOTHTrading) {
                this.overnightRect = new Rectangle(curX, curY, this.btnSize, this.btnSize);
                curX += this.imageRect.Width;
                separators.push(this.getVerticalSeparator(curX, curY));
            }
            else {
                this.overnightRect = Rectangle.Empty();
            }
            //#endregion IsAllowOTHTrading
            let shortDescriptionText = '';
            let leverage = '';
            if (!this.Collapsed) {
                shortDescriptionText = this.DescriptionText();
                curX = this.addTextRectWithSeparator(gr, shortDescriptionText, curX, curY, separators, (r) => {
                    this.infoRect = r;
                });
                leverage = this.getLeverageText();
                if (isValidString(leverage)) {
                    curX = this.addTextRectWithSeparator(gr, leverage, curX, curY, separators, (r) => {
                        this.leverageRect = r;
                    });
                }
                else {
                    this.leverageRect = Rectangle.Empty();
                }
                if (threeDotsMenuVisible) {
                    curX = this.addRectWithSeparator(curX, curY, this.btnSize, separators, (r) => {
                        this.actionButton = r;
                    });
                }
                else {
                    this.actionButton = Rectangle.Empty();
                }
                if (!param.TerceraChart.TerceraChartTradingToolsRenderer.NotTradingContext &&
                    (this.AllowCancelOrder() || this.AllowClosePosition()) &&
                    !TradingLockUtils.TradingLock.tradingLocked) {
                    curX = this.addRectWithSeparator(curX, curY, this.btnSize, separators, (r) => {
                        this.removeButton = r;
                    });
                }
                else {
                    this.removeButton = Rectangle.Empty();
                }
            }
            else {
                this.infoRect = Rectangle.Empty();
                this.leverageRect = Rectangle.Empty();
                this.removeButton = Rectangle.Empty();
                this.actionButton = Rectangle.Empty();
                this.slRect = Rectangle.Empty();
                this.tpRect = Rectangle.Empty();
            }
            this.mainContainer = new Rectangle(screenPoints00, curY, curX - screenPoints00, this.containerHeight);
            const { backgroundPen, backgroundBrush, TextBrush } = this.getDrawingTools(isBuy, hoveredOrSelected);
            gr.RoundRectangle(this.mainContainer, 4, backgroundBrush);
            this.drawIcon(gr, im, this.imageRect);
            this.drawCenteredText(gr, toolIconText, this.toolIconTextRect, TradingToolViewBase.tradeFont, TextBrush);
            this.drawIcon(gr, ThemeManager.NewImages.tableOrderOvernightIcon.image, this.overnightRect);
            this.drawCenteredText(gr, shortDescriptionText, this.infoRect, TradingToolViewBase.tradeFont, TextBrush);
            this.drawLiverage(gr, leverage, TextBrush);
            const actionBrush = this.getButtonBrush(isBuy, ToolSelectionType.Action === this.CurrentSelection.CurrentHover);
            this.drawImgButton(gr, this.actionButton, ThemeManager.NewImages.threeDotsMenuDefault, ThemeManager.NewImages.threeDotsMenuHovered, actionBrush);
            const removeBrush = this.getButtonBrush(isBuy, ToolSelectionType.Remove === this.CurrentSelection.CurrentHover);
            this.drawImgButton(gr, this.removeButton, ThemeManager.NewImages.closeIconDefault, ThemeManager.NewImages.closeIconHovered, removeBrush, RectangleEdgeFilter.All & (RectangleEdgeFilter.BottomRight | RectangleEdgeFilter.TopRight));
            this.drawSeparators(gr, separators);
            this.drawOrdersLinkingAtributes(gr, window, param);
            // limit price for stop limit order
            if (!this.Collapsed && this.Order.OrderType !== OrderType.StopLimit) {
                this.DrawSLTP(gr, param, maxX, true, this.imageRect, hoveredOrSelected);
                this.DrawSLTP(gr, param, maxX, false, this.imageRect, hoveredOrSelected);
            }
            const priceLinePen = this.getPriceLinePen(isBuy, isPosition);
            this.drawPriceLine(gr, this.mainContainer, maxX, screenPoints01, priceLinePen);
            const priceBrush = this.getPricePlateBrush(isBuy);
            this.tryAddPlateOnPriceScale(param, this.priceValue, this.Price, priceBrush);
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
    }
    DrawSLTP(gr, param, maxX, isSL, mainRectangle, hoveredOrSelected) {
        const isBuy = this.Order.BuySell === OperationType.Buy;
        const circleSize = 24;
        const spaceFromTradeContainer = 8;
        let SLTPRectDelta = 0;
        if ((isSL && !isBuy) || (!isSL && isBuy)) {
            SLTPRectDelta = -(spaceFromTradeContainer + circleSize);
        }
        else {
            SLTPRectDelta = this.containerHeight + spaceFromTradeContainer;
        }
        const sltpHolder = this.Order.createSlTpHolder();
        const PriceValue = isSL ? sltpHolder.StopLossPriceValue : sltpHolder.TakeProfitPriceValue;
        let workRectangle = isSL ? this.slRect : this.tpRect;
        const sltpPriceY = isSL ? this.slPriceY : this.tpPriceY;
        let orderSlTpPrice = isSL ? this.slPriceValue : this.tpPriceValue;
        const isSLTPAllowed = this.AllowSLTP(isSL);
        const isTradingContext = !param.TerceraChart.TerceraChartTradingToolsRenderer.NotTradingContext;
        if (isNaN(PriceValue) && isSLTPAllowed && isTradingContext) {
            const textBrush = this.getSLTPTextBrush(hoveredOrSelected);
            const backGroundBrush = this.getSLTPBackgroundBrush(isSL, hoveredOrSelected);
            workRectangle = mainRectangle.copy();
            workRectangle.X += this.indentationLineLength + this.btnSize / 2; // space from trade icon
            workRectangle.Width = this.isAllowOTHTradingSLTP ? circleSize * 2 : circleSize;
            workRectangle.Height = circleSize;
            if (isNaN(orderSlTpPrice)) {
                workRectangle.Y += SLTPRectDelta;
            }
            else {
                workRectangle.Y = Math.floor(sltpPriceY) - workRectangle.Height / 2;
            }
            this.drawPlaceSLTP(gr, workRectangle, isSL, this.isAllowOTHTradingSLTP, textBrush, backGroundBrush);
            this.drawSLTPLines(gr, workRectangle, isSL, mainRectangle);
            // sl info
            if (!isNaN(orderSlTpPrice)) {
                // calc data
                const ins = this.myRenderer.instrument;
                const fakeClosedOrderDescription = !this.errorMode
                    ? this.getFakeClosedOrderDescription(orderSlTpPrice, this.Order)
                    : Resources.getResource('chart.visualTrading.Invalid price');
                // calc rect
                let totalW = Math.floor(gr.GetTextWidth(fakeClosedOrderDescription, TradingToolViewBase.tradeFont)) +
                    this.paddingX * 2;
                if (totalW < 30) {
                    totalW = 30;
                }
                const sltpInfoRect = workRectangle.copy();
                sltpInfoRect.Width = totalW;
                sltpInfoRect.Height = this.containerHeight;
                sltpInfoRect.X += workRectangle.Width + 8; // 8 - space from sltp icon
                sltpInfoRect.Y += (workRectangle.Height - sltpInfoRect.Height) / 2;
                // drawing
                gr.RoundRectangle(sltpInfoRect, 4, backGroundBrush);
                this.drawCenteredText(gr, fakeClosedOrderDescription, sltpInfoRect, TradingToolViewBase.tradeFont, textBrush);
                // Line
                gr.DrawLine(new Pen(ThemeManager.colors.sltpChartHover), sltpInfoRect.X + sltpInfoRect.Width + 5, sltpInfoRect.Y + sltpInfoRect.Height / 2, maxX, sltpInfoRect.Y + sltpInfoRect.Height / 2); // 5 - space from circle to price line
                // Plate
                this.tryAddPlateOnPriceScale(param, orderSlTpPrice, orderSlTpPrice, backGroundBrush);
            }
        }
        else {
            workRectangle = Rectangle.Empty();
            orderSlTpPrice = NaN;
        }
        // Применяем измененные значения
        if (isSL) {
            this.slPriceY = sltpPriceY;
            this.slPriceValue = orderSlTpPrice;
            this.slRect = workRectangle;
        }
        else {
            this.tpPriceY = sltpPriceY;
            this.tpPriceValue = orderSlTpPrice;
            this.tpRect = workRectangle;
        }
    }
    // #region mouse
    /// <summary>
    /// Является ли тулза выделенной мышью (сейчас по линиям, но можно и весь полигон)
    /// </summary>
    IsSelectCheck(x, y) {
        if (this.TradeWasModified ||
            (!this.myRenderer.ShowPositions && !this.isOrderToolView) ||
            (!this.myRenderer.ShowOrders && this.isOrderToolView)) // тулза смещена и с сервера не пришло подтверждение не двигаем её
         {
            return false;
        }
        const newSelection = this.GetCurrentSelection(x, y);
        return newSelection !== ToolSelectionType.None;
    }
    GetCurrentSelection(x, y) {
        let newSelection = ToolSelectionType.None;
        if (this.infoRect.Contains(x, y)) {
            newSelection = ToolSelectionType.Info;
        }
        else if (this.leverageRect.Contains(x, y)) {
            newSelection = ToolSelectionType.Leverage;
        }
        else if (this.imageRect.Contains(x, y)) {
            newSelection = ToolSelectionType.Image;
        }
        else if (this.actionButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Action;
        }
        else if (this.removeButton.Contains(x, y)) {
            newSelection = ToolSelectionType.Remove;
        }
        else if (this.slRect.Contains(x, y)) {
            newSelection = ToolSelectionType.SL;
        }
        else if (this.tpRect.Contains(x, y)) {
            newSelection = ToolSelectionType.TP;
        }
        else if (Math.abs(y - this.screenPoints[0][1]) < ToolView.POINT_DX) {
            newSelection = ToolSelectionType.Line;
        }
        else if (this.mainContainer.Contains(x, y)) {
            newSelection = ToolSelectionType.Container;
        }
        else if (this.linkedOrderToolTipRect.Contains(x, y)) {
            newSelection = ToolSelectionType.LinkTooltip;
        }
        return newSelection;
    }
    CheckMinDistanceForStartMoving(x, y) {
        const mouseDownHover = this.CurrentSelection.MouseDownHover;
        if (!TradingLockUtils.TradingLock.tradingLocked &&
            (((mouseDownHover === ToolSelectionType.Image || mouseDownHover === ToolSelectionType.Info) &&
                this.AllowModify()) ||
                ((mouseDownHover === ToolSelectionType.SL || mouseDownHover === ToolSelectionType.TP) &&
                    this.AllowSLTP()))) {
            return Math.abs(this.CurrentMovement.PrevY - y) >= this.MinDistanceForStartMoving();
        }
        return false;
    }
    // start move
    ProcessMoveStart(x, y) {
        super.ProcessMoveStart(x, y);
        this.tpTrade = isNaN(this.Order.TakeProfitPriceValue)
            ? null
            : this.GetToolViewByOrderId(this.Order.OrderNumber + '-TP', TradingToolViewType.SLTPOrderToolView); // : this.GetToolViewByOrderId(this.Order.TPOrder.OrderNumber, TradingToolViewType.SLTPOrderToolView);
        this.slTrade = isNaN(this.Order.StopLossPriceValue)
            ? null
            : this.GetToolViewByOrderId(this.Order.OrderNumber + '-SL', TradingToolViewType.SLTPOrderToolView); // : this.GetToolViewByOrderId(this.Order.SLOrder.OrderNumber, TradingToolViewType.SLTPOrderToolView);
        this.sllTrade = this.slTrade ? this.slTrade.sllTool : null;
    }
    // move
    ProcessNewPosition(window, x, y, cashItemSeries = null) {
        const mouseDownHover = this.CurrentSelection.MouseDownHover;
        this.TradeWasModified = true;
        this.errorMode = false;
        let data = 0;
        data = window.PointsConverter.GetDataY(y);
        if (!isNullOrUndefined(cashItemSeries)) {
            data = cashItemSeries.settings.RevertConverter(window.PointsConverter.GetDataY(y));
        }
        const ins = this.myRenderer.Instrument;
        // move sl
        if (mouseDownHover === ToolSelectionType.SL) {
            this.slPriceValue = data;
            this.priceValue = this.Price;
            this.slPriceY = y;
            const roundedSL = ins.roundPrice(this.slPriceValue);
            const equalToEpsilon = MathUtils.equalToEpsilon(roundedSL, this.SlComparePrice());
            this.errorMode =
                equalToEpsilon ||
                    (this.Order.BuySell == OperationType.Buy
                        ? roundedSL >= this.SlComparePrice()
                        : roundedSL <= this.SlComparePrice());
        }
        // move tp
        else if (mouseDownHover === ToolSelectionType.TP) {
            this.tpPriceValue = data;
            this.priceValue = this.Price;
            this.tpPriceY = y;
            const roundedTP = ins.roundPrice(this.tpPriceValue);
            const equalToEpsilon = MathUtils.equalToEpsilon(roundedTP, this.TpComparePrice());
            this.errorMode =
                equalToEpsilon ||
                    (this.Order.BuySell == OperationType.Buy
                        ? roundedTP <= this.TpComparePrice()
                        : roundedTP >= this.TpComparePrice());
        }
        // move order
        else {
            if (this.AllowModify()) {
                super.ProcessNewPosition(window, x, y, cashItemSeries); // обновляем главного
                this.priceValue = data;
                this.priceY = y;
                const sltpHolder = this.Order.createSlTpHolder();
                // дочерние сл/тп тулзы ставим на новые координаты когда двигается родительская
                if (this.AllowSLTP() && (!sltpHolder.isSlEmpty() || !sltpHolder.isTpEmpty())) {
                    // Move child trades
                    if (this.slTrade != null) {
                        const isSlPercent = sltpHolder.StopLossPriceType === SlTpPriceType.Percent;
                        const slOffset = sltpHolder.isSlAbsolute()
                            ? SlTpUtils.calculateTicks(this.Order.Price, sltpHolder.StopLossPriceValue, this.Order.Instrument)
                            : sltpHolder.StopLossPriceValue;
                        const newBasePrice = this.Order.Instrument.roundPrice(this.Order.OrderType === OrderType.StopLimit
                            ? this.priceValue - (this.Order.StopLimit - this.Order.Price)
                            : this.priceValue);
                        const newSlPrice = SlTpUtils.getSLTPPrice(this.Order.Instrument, this.Order.BuySell === OperationType.Buy, true, newBasePrice, slOffset, isSlPercent ? SlTpPriceType.Percent : SlTpPriceType.Offset);
                        this.slTrade.priceValue = newSlPrice;
                        this.slTrade.priceY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.slTrade.priceValue))));
                        this.slTrade.TradeWasModified = true;
                        this.slTrade.parentValue = newBasePrice;
                        this.slTrade.parentY = y;
                        if (this.sllTrade != null) {
                            const sllOffset = sltpHolder.isSlAbsolute()
                                ? SlTpUtils.calculateTicks(sltpHolder.StopLossPriceValue, sltpHolder.StopLossLimitPriceValue, this.Order.Instrument)
                                : sltpHolder.StopLossLimitPriceValue;
                            const newBasePrice = this.Order.Instrument.roundPrice(this.Order.OrderType === OrderType.StopLimit
                                ? this.priceValue - (this.Order.StopLimit - this.Order.Price)
                                : this.priceValue);
                            const newSllPrice = SlTpUtils.getSLLPrice(this.Order.Instrument, this.Order.BuySell === OperationType.Buy, newBasePrice, slOffset, sllOffset, SlTpPriceType.Offset);
                            this.sllTrade.priceValue = newSllPrice;
                            this.sllTrade.priceY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.sllTrade.priceValue))));
                            this.sllTrade.TradeWasModified = true;
                            if (this.slTrade != null) {
                                this.sllTrade.parentValue = this.slTrade.priceValue;
                                this.sllTrade.parentY = this.slTrade.priceY;
                            }
                        }
                    }
                    if (this.tpTrade != null) {
                        const isTpPercent = this.Order.StopLossPriceType === SlTpPriceType.Percent;
                        const tpOffset = sltpHolder.isTpAbsolute()
                            ? SlTpUtils.calculateTicks(this.Order.Price, sltpHolder.TakeProfitPriceValue, this.Order.Instrument)
                            : sltpHolder.TakeProfitPriceValue;
                        const newBasePrice = this.Order.Instrument.roundPrice(this.Order.OrderType === OrderType.StopLimit
                            ? this.priceValue - (this.Order.StopLimit - this.Order.Price)
                            : this.priceValue);
                        const newTpPrice = SlTpUtils.getSLTPPrice(this.Order.Instrument, this.Order.BuySell === OperationType.Buy, false, newBasePrice, tpOffset, isTpPercent ? SlTpPriceType.Percent : SlTpPriceType.Offset);
                        this.tpTrade.priceValue = newTpPrice;
                        this.tpTrade.priceY = Math.floor(Math.round(window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.tpTrade.priceValue))));
                        this.tpTrade.TradeWasModified = true;
                        this.tpTrade.parentValue = newBasePrice;
                        this.tpTrade.parentY = y;
                    }
                }
            }
        }
    }
    // mouse finish
    ProcessMoveFinish() {
        try {
            super.ProcessMoveFinish();
            const mouseDownHover = this.CurrentSelection.MouseDownHover;
            if (this.errorMode) {
                this.slPlased = false;
                this.tpPlased = false;
                this.TradeWasModified = false;
                this.CancelMoving();
                return;
            }
            if (mouseDownHover === ToolSelectionType.Image ||
                mouseDownHover === ToolSelectionType.Info ||
                mouseDownHover == ToolSelectionType.Line ||
                mouseDownHover === ToolSelectionType.SL ||
                mouseDownHover === ToolSelectionType.TP) {
                // new SL
                if (mouseDownHover === ToolSelectionType.SL) {
                    const slPriceValue = this.myRenderer.Instrument != null
                        ? this.myRenderer.Instrument.roundPrice(this.slPriceValue)
                        : this.slPriceValue;
                    this.terceraChartPanelContext.ChartVisualTrading(this.Order, {
                        price: slPriceValue,
                        isAllowOTHTrading: this.isAllowOTHTradingSLTP,
                        action: TerceraChartTradingOperation.PlaceSL,
                        CancelCallback: this.undoChanges.bind(this)
                    });
                    this.TradeWasModified = false;
                    this.slPlased = true;
                }
                // new TP
                else if (mouseDownHover === ToolSelectionType.TP) {
                    const tpPriceValue = this.myRenderer.Instrument != null
                        ? this.myRenderer.Instrument.roundPrice(this.tpPriceValue)
                        : this.tpPriceValue;
                    this.terceraChartPanelContext.ChartVisualTrading(this.Order, {
                        price: tpPriceValue,
                        isAllowOTHTrading: this.isAllowOTHTradingSLTP,
                        action: TerceraChartTradingOperation.PlaceTP,
                        CancelCallback: this.undoChanges.bind(this)
                    });
                    this.TradeWasModified = false;
                    this.tpPlased = true;
                }
                // Modify order price, and child sl/tp orders
                else {
                    const newPrice = this.myRenderer.Instrument != null
                        ? this.myRenderer.Instrument.roundPrice(this.priceValue)
                        : this.priceValue;
                    const cancel = new CancelCB();
                    cancel.add(this.undoChanges.bind(this));
                    if (this.slTrade) {
                        this.slTrade.lastValidParentValue = newPrice;
                        cancel.add(this.slTrade.Updated.bind(this.slTrade));
                    }
                    if (this.tpTrade) {
                        this.tpTrade.lastValidParentValue = newPrice;
                        cancel.add(this.tpTrade.Updated.bind(this.tpTrade));
                    }
                    if (this.sllTrade) {
                        this.sllTrade.lastValidParentValue = this.sllTrade.priceValue;
                        cancel.add(this.sllTrade.Updated.bind(this.sllTrade));
                    }
                    this.lastValidPriceValue = newPrice;
                    this.terceraChartPanelContext.ChartVisualTrading(this.Order, {
                        price: newPrice,
                        isLimitModify: false,
                        action: TerceraChartTradingOperation.MoveOrder,
                        CancelCallback: cancel.Run.bind(cancel)
                    });
                }
            }
            else {
                super.ProcessMoveFinish();
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
            this.ResetMouse();
            super.ProcessMoveFinish();
        }
    }
    CancelMove() {
        this.FinishMove();
        this.Updated();
    }
    ResetMove() {
        if (this.slTrade != null) {
            this.slTrade.lastValidPriceValue = NaN;
            this.slTrade.lastValidParentValue = NaN;
        }
        if (this.sllTrade != null) {
            this.sllTrade.lastValidPriceValue = NaN;
            this.sllTrade.lastValidParentValue = NaN;
        }
        if (this.tpTrade != null) {
            this.tpTrade.lastValidPriceValue = NaN;
            this.tpTrade.lastValidParentValue = NaN;
        }
        this.lastValidPriceValue = NaN;
        this.ResetMouse();
        this.tpTrade = null;
        this.slTrade = null;
        this.sllTrade = null;
    }
    FinishMove() {
        if (this.errorMode) {
            this.slPlased = false;
            this.tpPlased = false;
        }
        this.TradeWasModified = false;
        super.ProcessMoveFinish();
        this.ResetMove();
        this.MouseDownHover = ToolSelectionType.None;
    }
    // mouse click
    ProcessClick(e) {
        if (this.CurrentSelection.MouseDownHover === this.CurrentSelection.CurrentHover) {
            switch (this.CurrentSelection.MouseDownHover) {
                case ToolSelectionType.Image:
                case ToolSelectionType.Leverage:
                    this.Collapsed = !this.Collapsed;
                    break;
                case ToolSelectionType.Info:
                    if (this.Order.OrderType === OrderType.StopLimit) {
                        this.Collapsed = !this.Collapsed;
                    }
                    else if (!TradingLockUtils.TradingLock.tradingLocked && !this.Collapsed) {
                        this.terceraChartPanelContext.ChartVisualTrading(this.Order, {
                            action: TerceraChartTradingOperation.ModifyOrderQuantity,
                            rect: this.infoRect,
                            quantity: OrderFormatter.Amount(this.Order),
                            orderToolView: this
                        });
                    }
                    break;
                case ToolSelectionType.Action: {
                    // show menu
                    const actionProcessor = this.terceraChartPanelContext.terceraChartRactive.terceraChart.TerceraChartActionProcessor;
                    const menuItems = [];
                    if (!TradingLockUtils.TradingLock.tradingLocked) {
                        menuItems.push(actionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.ChangeToMarket, this.Order)));
                    }
                    if (isValidArray(menuItems)) {
                        showContextMenu({ x: e.ClientLocation.X, y: e.ClientLocation.Y, items: menuItems });
                    }
                    // if (!Utils.TradingLock.tradingLocked)
                    //     this.terceraChartPanelContext.ChartVisualTrading(this.Order, { action: TerceraChartTradingOperation.ActionButton, isPosition: false });
                    break;
                }
                case ToolSelectionType.Remove:
                    if (!TradingLockUtils.TradingLock.tradingLocked) {
                        this.terceraChartPanelContext.ChartVisualTrading(this.Order, {
                            action: TerceraChartTradingOperation.CancelOrder
                        });
                    }
                    break;
                case ToolSelectionType.LinkTooltip:
                    this.terceraChartPanelContext.ChartVisualTrading(this.Order, {
                        action: TerceraChartTradingOperation.ScrollToSelectedPrice,
                        price: this.Order.Price
                    });
                    break;
                case ToolSelectionType.Line:
                case ToolSelectionType.SL:
                case ToolSelectionType.TP:
                case ToolSelectionType.None:
                case ToolSelectionType.Trailing:
                default:
                    break;
            }
        }
        this.ResetMouse();
    }
    ResetMouse() {
        this.CurrentSelection.MouseDownHover = ToolSelectionType.None;
        this.errorMode = false;
        this.priceValue = NaN;
        // 4. Дергание при торговле
        if (!this.slPlased) {
            this.slPriceValue = this.slPriceY = NaN;
        }
        if (!this.tpPlased) {
            this.tpPriceValue = this.tpPriceY = NaN;
        }
        if (this.slTrade != null) {
            this.slTrade.parentValue = NaN;
        }
        if (this.sllTrade != null) {
            this.sllTrade.parentValue = NaN;
        }
        if (this.tpTrade != null) {
            this.tpTrade.parentValue = NaN;
        }
    }
    GetCursor(e) {
        const curElement = this.CurrentSelection.MouseDownHover === ToolSelectionType.None
            ? this.CurrentSelection.CurrentHover
            : this.CurrentSelection.MouseDownHover;
        switch (curElement) {
            case ToolSelectionType.Image:
                return this.AllowModify() ? Cursors.NoMoveVert : null;
            case ToolSelectionType.Info:
                return Cursors.Hand;
            case ToolSelectionType.SL:
            case ToolSelectionType.TP:
                return this.AllowSLTP() ? (this.errorMode ? Cursors.No : Cursors.NoMoveVert) : null;
            case ToolSelectionType.Action:
            case ToolSelectionType.Remove:
            case ToolSelectionType.Line:
            case ToolSelectionType.None:
            case ToolSelectionType.Trailing:
            default:
                return null;
        }
    }
    GetTooltip(e) {
        let res = '';
        switch (this.CurrentSelection.CurrentHover) {
            case ToolSelectionType.Image:
                res = Resources.getResource(this.IsPositionToolView() ? 'TradeTool.Position' : 'TradeTool.Order');
                break;
            case ToolSelectionType.Info:
                res = Resources.getResource(this.IsPositionToolView() ? 'TradeTool.Position P/L' : 'TradeTool.Amount of order');
                break;
            case ToolSelectionType.Remove:
                res = Resources.getResource(this.IsPositionToolView() ? 'TradeTool.Close position' : 'TradeTool.Cancel order');
                break;
            case ToolSelectionType.Action:
                res = Resources.getResource('TradeTool.ChangeToMarket');
                break;
            case ToolSelectionType.Leverage:
                res = Resources.getResource('TradeTool.Leverage');
                break;
            default:
                res = null;
                break;
        }
        return res || super.GetTooltip(e);
    }
    // #endregion
    Dispose() {
        this.slTrade = null;
        this.sllTrade = null;
        this.tpTrade = null;
        this.GetToolViewByOrderId = null;
        this.Order = null;
        super.Dispose();
    }
    getToolTipDescription() {
        const ins = this.Order.Instrument;
        return ins.formatPrice(this.Order.Price);
    }
    getFakeClosedOrderDescription(fakeClosedOrderPrice, order) {
        const ins = order.Instrument;
        let basePriceRaw;
        if (order.Price > 0) {
            basePriceRaw = order.Price;
        }
        else if (this.Price > 0) {
            basePriceRaw = this.Price;
        }
        else {
            basePriceRaw = this.priceValue;
        }
        const basePrice = ins.roundPrice(basePriceRaw);
        const closePrice = ins.roundPrice(fakeClosedOrderPrice);
        const absoluteOffset = closePrice - basePrice;
        const pnl = this.Order.Account.formatPrice(TradingToolViewBase.FuturesSlTp(order, absoluteOffset));
        const ticksVisualOffset = SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(ins);
        const offsetTicks = SlTpUtils.calculateTicks(basePrice, closePrice, ins);
        const offsetPercent = SlTpUtils.calculatePercent(basePrice, closePrice);
        const offsetTicksStr = `${SlTpUtils.formatSLTPValue(SlTpUtils.toVisualValue(offsetTicks, ins, ticksVisualOffset), ins, ticksVisualOffset)}${SlTpUtils.getSymbolForOffsetMode(ticksVisualOffset)}`;
        const offsetPercentStr = `${SlTpUtils.formatSLTPValue(SlTpUtils.toVisualValue(offsetPercent, ins, OffsetModeViewEnum.Percent), ins, OffsetModeViewEnum.Percent)}${SlTpUtils.getSymbolForOffsetMode(OffsetModeViewEnum.Percent)}`;
        return `${pnl} ${offsetTicksStr} ${offsetPercentStr}`;
    }
    getDrawingTools(isBuy, hoveredOrSelected) {
        let { backgroundPen, backgroundBrush, TextBrush } = super.getDrawingTools(isBuy, hoveredOrSelected);
        if (this.isLinkingStyle) {
            TextBrush = TradingToolViewBase.linkedStyleFontBrush;
            backgroundBrush = hoveredOrSelected
                ? TradingToolViewBase.linkedStyleHoverBrush
                : TradingToolViewBase.linkedStyleDefaultBrush;
            backgroundPen = Pens.Black;
        }
        return { backgroundPen, backgroundBrush, TextBrush };
    }
    getPriceLinePen(isBuy, isPosition) {
        if (!this.TradeWasModified) {
            if (isBuy) {
                return isPosition ? this.myRenderer.positionBuyPen : this.myRenderer.orderBuyPen;
            }
            else {
                return isPosition ? this.myRenderer.positionSellPen : this.myRenderer.orderSellPen;
            }
        }
        else {
            return TradingToolViewBase.chartOrderModifiedPen;
        }
    }
    getButtonBrush(isBuy, isHovered, isActive) {
        if (this.isLinkingStyle) {
            return isHovered ? TradingToolViewBase.linkedTradeToolButtonBackgroundHoveredBrush : null;
        }
        else {
            return super.getButtonBrush(isBuy, isHovered, isActive);
        }
    }
    clearAllRectangles() {
        this.infoRect = Rectangle.Empty();
        this.leverageRect = Rectangle.Empty();
        this.removeButton = Rectangle.Empty();
        this.actionButton = Rectangle.Empty();
        this.slRect = Rectangle.Empty();
        this.tpRect = Rectangle.Empty();
        this.imageRect = Rectangle.Empty();
        this.mainContainer = Rectangle.Empty();
        this.toolIconTextRect = Rectangle.Empty();
        this.linkedOrderToolTipRect = Rectangle.Empty();
    }
    getOCOSecondOrderTool(parentOrder) {
        if (isNullOrUndefined(parentOrder) || parentOrder.isPosition) {
            return null;
        }
        const parentToolViewType = this.myRenderer.getToolViewTypeByOrder(parentOrder);
        return this.GetToolViewByOrderId(parentOrder.OrderNumber, parentToolViewType);
    }
    // Draw tooltip for invisible part of order
    DrawLinkToOrder(gr, isTop, tooltipX, tooltipY, orderType, infoStr) {
        const triangleHeight = 5;
        const triangleHalfWidth = 5;
        let curX = tooltipX;
        const curY = isTop ? tooltipY + triangleHeight : tooltipY - triangleHeight - this.containerHeight;
        const hoverOrSelected = this.CurrentSelection.CurrentState !== SelectionState.None;
        const linkedBrush = hoverOrSelected
            ? TradingToolViewBase.linkedStyleHoverBrush
            : TradingToolViewBase.linkedStyleDefaultBrush;
        const textBrush = TradingToolViewBase.linkedStyleFontBrush;
        const img = hoverOrSelected
            ? ThemeManager.NewImages.linkOrderOnChartHover.image
            : ThemeManager.NewImages.linkOrderOnChartDefault.image;
        const imgRectangle = new Rectangle(curX, curY, this.btnSize, this.btnSize);
        curX += imgRectangle.Width;
        const orderText = this.getToolIconText(orderType);
        const orderTextRect = new Rectangle(curX, curY, Math.floor(gr.GetTextWidth(orderText, TradingToolViewBase.tradeFont)) + this.paddingX * 2, this.containerHeight);
        curX += orderTextRect.Width;
        const infoRect = new Rectangle(curX, curY, Math.floor(gr.GetTextWidth(infoStr, TradingToolViewBase.tradeFont)) + this.paddingX * 2, this.containerHeight);
        curX += infoRect.Width;
        const linkRectangle = new Rectangle(tooltipX, curY, curX - tooltipX, this.containerHeight);
        const center = linkRectangle.X + linkRectangle.Width / 2;
        const y = isTop ? linkRectangle.Y + 1 : linkRectangle.Y + linkRectangle.Height; // + 1 for correct drawing of triangle
        gr.RoundRectangle(linkRectangle, 4, linkedBrush);
        gr.DrawCenteredImage(img, imgRectangle);
        this.drawCenteredText(gr, orderText, orderTextRect, TradingToolViewBase.tradeFont, textBrush);
        this.drawCenteredText(gr, infoStr, infoRect, TradingToolViewBase.tradeFont, textBrush);
        gr.FillPolygon(linkedBrush, [
            new Point(center - triangleHalfWidth, y),
            new Point(center + triangleHalfWidth, y),
            new Point(center, isTop ? y - triangleHeight : y + triangleHeight)
        ]);
        return linkRectangle;
    }
    getToolIconText(orderType) {
        switch (orderType) {
            case OrderType.Market:
                return 'MKT';
            case OrderType.Limit:
            case OrderType.Care:
                return 'LMT';
            case OrderType.StopLimit:
            case OrderType.Stop:
                return 'STP';
            case OrderType.TrailingStop:
                return 'Tr-s';
            default:
                return null;
        }
    }
    getCorrectOrderX(x, minX) {
        return this.myRenderer.ShowVisualTradingOnLeftSide || x < minX ? minX : x;
    }
    //#region Orders linking
    drawOrdersLinkingAtributes(gr, window, param) {
        const bindOrder = this.Order.getParentOrder();
        const bindOrderToolView = this.getOCOSecondOrderTool(bindOrder);
        if (isNullOrUndefined(bindOrderToolView) || bindOrderToolView.isDrawOrdersLinking)
            return;
        bindOrderToolView.UpdateScreenPoints(window, param.TerceraChart.MainCashItemSeries);
        const CR = window.ClientRectangle;
        let { x: bindOrderPriceX, y: bindOrderPriceY } = OrderToolView.getOrderToolCoordinates(bindOrderToolView, window);
        const isBindOrderVisible = CR.Contains(bindOrderPriceX, bindOrderPriceY);
        if (!this.MainOrderVisibility && !isBindOrderVisible)
            return;
        if (!isBindOrderVisible) {
            this.linkedOrderToolTipRect = this.drawLinkForHiddenOrder(gr, bindOrder, bindOrderToolView, CR);
            bindOrderPriceX = this.linkedOrderToolTipRect.X;
            bindOrderPriceY = this.linkedOrderToolTipRect.Y + this.linkedOrderToolTipRect.Height / 2;
        }
        else {
            this.linkedOrderToolTipRect = Rectangle.Empty();
        }
        const { x: orderX, y: orderY } = OrderToolView.getOrderToolCoordinates(this, window);
        const isTopCoef = orderY > bindOrderPriceY ? 1 : -1;
        const pen = new Pen(ThemeManager.colors.chartOCOLineBackground);
        if (orderX < bindOrderPriceX) {
            const fromPoint = new Point(orderX + this.btnSize / 2, orderY - (this.containerHeight / 2) * isTopCoef);
            const toPoint = new Point(bindOrderPriceX, bindOrderPriceY);
            this.drawElbowLine(gr, fromPoint, toPoint, pen);
        }
        else if (orderX > bindOrderPriceX) {
            const fromPoint = new Point(bindOrderPriceX + this.btnSize / 2, bindOrderPriceY + (this.containerHeight / 2) * isTopCoef);
            const toPoint = new Point(orderX, orderY);
            this.drawElbowLine(gr, fromPoint, toPoint, pen);
        }
        else {
            const fromPoint = new Point(orderX + this.btnSize / 2, orderY - (this.containerHeight / 2) * isTopCoef);
            const toPoint = new Point(bindOrderPriceX + this.btnSize / 2, bindOrderPriceY + (this.containerHeight / 2) * isTopCoef);
            gr.DrawLine(pen, fromPoint.X, fromPoint.Y, toPoint.X, toPoint.Y);
        }
        this.isDrawOrdersLinking = true;
    }
    drawElbowLine(gr, fromPoint, toPoint, pen) {
        const edgePoint = new Point(fromPoint.X, toPoint.Y);
        const points = [fromPoint, edgePoint, toPoint];
        gr.DrawLines(pen, points);
    }
    drawLinkForHiddenOrder(gr, hiddenOrder, hiddenOrderView, screenRect) {
        this.CurrentSelection.CurrentState = hiddenOrderView.CurrentSelection.CurrentState;
        const minX = screenRect.X;
        let x0 = this.screenPoints[0][0];
        if (!isValidNumber(x0) || x0 < minX || this.myRenderer.ShowVisualTradingOnLeftSide) {
            x0 = minX;
        }
        const hiddenPrice = this.getOrderPrice(hiddenOrder);
        const otherPrice = this.getOrderPrice(this.Order);
        const isTop = hiddenPrice > otherPrice;
        const tooltipX = hiddenOrder.OrderType === OrderType.StopLimit && hiddenOrder.isActivated ? x0 + this.getPlateOffset() : x0;
        const tooltipY = isTop ? screenRect.Y : screenRect.Y + screenRect.Height;
        const descr = hiddenOrderView.getToolTipDescription();
        return this.DrawLinkToOrder(gr, isTop, tooltipX, tooltipY, hiddenOrder.OrderType, descr);
    }
    getOrderPrice(order) {
        if (order.OrderType === OrderType.StopLimit && !order.isActivated) {
            return order.StopLimit;
        }
        else {
            return order.Price;
        }
    }
    static getOrderToolCoordinates(orderTool, window) {
        const CR = window.ClientRectangle;
        const order = orderTool.Order;
        const orderScreenPoints00 = orderTool.getCorrectOrderX(orderTool.screenPoints[0][0], CR.X);
        if (order.OrderType === OrderType.StopLimit && order.isActivated) {
            const cashItemSeriesSettings = orderTool.myRenderer.chart.cashItemSeriesSettings;
            const x = orderScreenPoints00 + orderTool.getPlateOffset();
            const y = OrderToolViewBase.getYByPrice(window, cashItemSeriesSettings, order.Price);
            return { x, y };
        }
        else {
            return { x: orderScreenPoints00, y: orderTool.screenPoints[0][1] };
        }
    }
    //#endregion
    drawSLTPLines(gr, workRectangle, isSL, mainRectangle) {
        const pen = TradingToolViewBase.sltpChartPen;
        if (this.Order.BuySell === OperationType.Sell) {
            if (isSL) {
                gr.DrawLine(pen, workRectangle.X - this.indentationLineLength, workRectangle.Y + workRectangle.Height / 2, workRectangle.X - this.indentationLineLength, mainRectangle.Y);
            }
            else {
                gr.DrawLine(pen, workRectangle.X - this.indentationLineLength, workRectangle.Y + workRectangle.Height / 2, workRectangle.X - this.indentationLineLength, mainRectangle.Y + mainRectangle.Height);
            }
            gr.DrawLine(pen, workRectangle.X - this.indentationLineLength, workRectangle.Y + workRectangle.Height / 2, workRectangle.X, workRectangle.Y + workRectangle.Height / 2);
        }
        else {
            if (isSL) {
                gr.DrawLine(pen, workRectangle.X - this.indentationLineLength, workRectangle.Y + workRectangle.Height / 2, workRectangle.X - this.indentationLineLength, mainRectangle.Y + mainRectangle.Height);
            }
            else {
                gr.DrawLine(pen, workRectangle.X - this.indentationLineLength, workRectangle.Y + workRectangle.Height / 2, workRectangle.X - this.indentationLineLength, mainRectangle.Y);
            }
            gr.DrawLine(pen, workRectangle.X - this.indentationLineLength, workRectangle.Y + workRectangle.Height / 2, workRectangle.X, workRectangle.Y + workRectangle.Height / 2);
        }
    }
    drawPlaceSLTP(gr, rect, isSL, isOvernight, textBrush, circleBrush) {
        const sltpText = isSL ? 'SL' : 'TP';
        if (isOvernight) {
            const slRect = new Rectangle(rect.X, rect.Y, rect.Width / 2, rect.Height);
            const overnightRect = new Rectangle(slRect.Right(), rect.Y, rect.Width / 2, rect.Height);
            gr.RoundRectangle(rect, 5, circleBrush);
            this.drawSeparators(gr, [new Rectangle(overnightRect.X - 1, overnightRect.Y, 0, overnightRect.Height)]);
            this.drawCenteredText(gr, sltpText, slRect, TradingToolViewBase.tradeFont, textBrush);
            this.drawIcon(gr, ThemeManager.NewImages.tableOrderOvernightIcon.image, overnightRect);
        }
        else {
            gr.FillEllipse(circleBrush, rect.X, rect.Y, rect.Width, rect.Height);
            this.drawCenteredText(gr, sltpText, rect, TradingToolViewBase.tradeFont, textBrush);
        }
    }
    drawLiverage(gr, leverage, TextBrush) {
        if (this.leverageRect.IsEmpty()) {
            return;
        }
        this.drawCenteredText(gr, leverage, this.leverageRect, TradingToolViewBase.tradeFont, TextBrush);
    }
    getOrderToolIcon(isHoveredOrSelected) {
        const isPosition = this.Order.isPosition;
        if (isPosition) {
            return isHoveredOrSelected
                ? ThemeManager.NewImages.positionOnChartIconHovered.image
                : ThemeManager.NewImages.positionOnChartIconDefault.image;
        }
        else {
            if ((this.Order.OrderType === OrderType.StopLimit && this.isLinkingStyle) ||
                this.Order.BoundTo !== NO_BIND_TO) {
                return isHoveredOrSelected
                    ? ThemeManager.NewImages.linkOrderOnChartHover.image
                    : ThemeManager.NewImages.linkOrderOnChartDefault.image;
            }
            else {
                return isHoveredOrSelected
                    ? ThemeManager.NewImages.orderOnChartHovered.image
                    : ThemeManager.NewImages.orderOnChartDefault.image;
            }
        }
    }
    getSLTPBackgroundBrush(isSL, hoveredOrSelected) {
        const isPlaced = isSL ? this.slPlased : this.tpPlased;
        if (isPlaced) {
            return hoveredOrSelected
                ? TradingToolViewBase.sltpChartHoverBrush
                : TradingToolViewBase.sltpChartDefaultBrush;
        }
        return hoveredOrSelected
            ? TradingToolViewBase.chartOrderModifiedHoveredBrush
            : TradingToolViewBase.chartOrderModifiedBrush;
    }
    getSLTPTextBrush(isHovered) {
        if (isHovered) {
            return new SolidBrush(ThemeManager.colors.sltpChartForegroundHovered);
        }
        else {
            return new SolidBrush(ThemeManager.colors.sltpChartForeground);
        }
    }
}
export class CancelCB {
    constructor() {
        this.methodArr = [];
    }
    add(method) {
        this.methodArr.push(method);
    }
    Run() {
        for (let i = 0; i < this.methodArr.length; i++) {
            this.methodArr[i]();
        }
    }
}
